/**
 * PerformanceSection — Delegate rodeo nested-performance row.
 *
 * Purpose
 *   - Render a single `rodeo_performance` as a collapsible sub-row inside
 *     an expanded `RodeoListItem`.
 *   - Show the performance's events (hydrated from the `events` table) in
 *     the same visual style as jackpot event rows.
 *   - Own the destructive confirmation dialogs for BOTH performance and
 *     event deletes, and invoke the shared delete mutations
 *     (`useDeletePerformance` / `useDeleteEvent`) on confirm.
 *
 * Visual style
 *   - Header mirrors `RodeoListItem`'s clickable row but one indent level
 *     deeper (subtle left border + slightly lighter background) so the
 *     nesting is obvious without fighting the parent card chrome.
 *   - Event rows reuse the same `border-2 / bg-muted/30 / group-hover`
 *     treatment as `JackpotListRow`'s internal `EventRow` to keep the
 *     delegate surfaces consistent.
 *
 * Ownership & data
 *   - Local `expanded` state (unlike `RodeoListItem`, which is controlled by
 *     its parent page). Performances inside one expanded rodeo may be opened
 *     independently, so each section owns its own collapse state.
 *   - Events are loaded in two layers:
 *       1. `useEventsByPerformance` returns join rows (id, event_id …)
 *       2. `useQueries` hydrates each `event_id` via the generated
 *          `EventsClient.getById` so we can show event name and type.
 *     This keeps the component self-sufficient even when the parent rodeo
 *     payload does not include nested event objects.
 *
 * Edit Performance modal — events slot
 *   - Renders `PersistedEventTogglesSlot`: a multi-select toggle list of
 *     every event on the parent rodeo, with a master toggle at the top.
 *     Toggling adds/removes a `performance_events` join row immediately.
 *     Per-event edit/delete UX still lives on the expanded perf row's
 *     event list (PerformanceEventRow + RodeoEventDialog), not in the
 *     modal slot.
 *
 * Notes on confirmation UI
 *   - The design calls for an "AlertDialog", but the app's component library
 *     does not include one. We use the shared `Dialog` instead — same modal
 *     mechanics, same copy shape as `DelegateRodeosPage`.
 */

import { useState, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  ChevronRight,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  Trophy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEventsByPerformance } from '@/hooks/useEventsByPerformance';
// useEventConfigsByPerformance was removed — the legacy event-configs
// list is now derived from hydratedEvents in the component body.
import { useDeletePerformance } from '@/hooks/useDeletePerformance';
import { useDeleteEvent } from '@/hooks/useDeleteEvent';
import { EventsClient } from '@/api/generated_clients/generated-events-api-client';
import {
  eventsKeys,
  useCreateEvents,
  useUpdateEvents,
} from '@/api/generated_hooks/generated-events-hooks';
import { useUpdatePerformances } from '@/api/generated_hooks/generated-performances-hooks';
import {
  useCreatePerformanceEvents,
  usePerformanceEventsClient,
} from '@/api/generated_hooks/generated-performance_events-hooks';
import { rodeoRoundConfigsKeys } from '@/api/generated_hooks/generated-rodeo_round_configs-hooks';
import { httpClient } from '@/api/http-client';
import { useRoundConfigsByEvent } from '@/hooks/useRoundConfigsByEvent';
import { useRoundConfigsByPerformance } from '@/hooks/useRoundConfigsByPerformance';
import { useQueryClient } from '@tanstack/react-query';
import type { PerformancesRow } from '@/types/generated_schemas/generated-performances-schema';
import type { EventsRow } from '@/types/generated_schemas/generated-events-schema';
import { formatDate } from '@/utils/formatters';
import {
  PerformanceDialog,
  type PerformanceDialogValues,
} from '@/components/rodeo-wizard/PerformanceDialog';
import {
  RodeoEventDialog,
  type RodeoEventDialogValues,
} from '@/components/rodeo-wizard/RodeoEventDialog';
import { PersistedEventTogglesSlot } from '@/components/delegate/PersistedEventTogglesSlot';

export interface PerformanceSectionProps {
  /**
   * The rodeo_performance record, typed against the generated schema so the
   * parent cannot accidentally pass a wizard-draft shape (performance_key /
   * event_type etc. are distinct and incompatible).
   */
  performance: PerformancesRow;

  /** Parent rodeo id — required for the event edit navigation. */
  rodeoId: string;

  /**
   * Optional hook fired AFTER the performance delete mutation succeeds.
   * The component always runs `useDeletePerformance.mutateAsync` itself;
   * this callback exists so the parent page can react (analytics, toast,
   * collapse the parent row, etc.).
   */
  onDeletePerformance?: (id: string) => void;

  /** Same contract as onDeletePerformance but for single-event deletes. */
  onDeleteEvent?: (id: string) => void;

  /**
   * Optional override for the edit-event action. When absent the default
   * behavior is `navigate('/delegate/rodeos/:rodeoId/edit')`. There is no
   * per-event route today, so navigating to the wizard is the minimum
   * viable "edit event" surface.
   */
  onEditEvent?: (eventId: string) => void;

  /**
   * When false, the row renders as a simple non-expandable card — no
   * chevron, no events list. Use this when the caller has already
   * drilled in via a discipline (EventSection), since showing
   * the event list inside the perf would duplicate what the user
   * already sees. Defaults to true for backward compat.
   */
  expandable?: boolean;
}


/**
 * Format a Postgres `time` column (HH:MM:SS[±tz]) as "h:mm AM/PM".
 * Returns an empty string when the input is falsy/invalid so callers can
 * render inside a truthy-guarded block without noise.
 */
function formatTime(time: unknown): string {
  if (!time) return '';
  const str = String(time);
  // Parse HH:MM from the start of the string — tolerate trailing seconds / tz.
  const match = /^(\d{1,2}):(\d{2})/.exec(str);
  if (!match) return '';
  const hours24 = Number(match[1]);
  const minutes = match[2];
  if (Number.isNaN(hours24) || hours24 < 0 || hours24 > 23) return '';
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${minutes} ${period}`;
}

export function PerformanceSection({
  performance,
  rodeoId,
  onDeletePerformance,
  onDeleteEvent,
  onEditEvent: _onEditEvent,
  expandable = true,
}: PerformanceSectionProps) {
  const [expanded, setExpanded] = useState(false);

  // Confirmation-dialog state. Using two booleans (rather than one string key)
  // keeps each dialog's props simple and makes the render tree easy to reason
  // about in tests.
  const [showDeletePerformance, setShowDeletePerformance] = useState(false);
  const [deletePerformanceError, setDeletePerformanceError] = useState<string | null>(null);
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null);
  const [deleteEventError, setDeleteEventError] = useState<string | null>(null);

  // Edit-performance dialog state (separate from delete — the dialog reuses
  // the same component used in RodeoWizard Step 2 so the add/edit UX matches
  // across surfaces).
  const [showEditPerformance, setShowEditPerformance] = useState(false);
  const [editPerformanceError, setEditPerformanceError] = useState<string | null>(null);

  // Event-config edit state — opened from PerformanceEventRow in the
  // expanded list view (NOT from the Edit Performance modal — that surface
  // now uses PersistedEventTogglesSlot for assignment-only editing).
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [eventDialogError, setEventDialogError] = useState<string | null>(null);

  // Event-config delete confirmation state — mirrors the Delete Performance
  // dialog below so destructive actions always route through a confirm step.
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [deletingEventError, setDeletingEventError] = useState<string | null>(null);

  // Mutations owned internally — parent never has to wire these up.
  const deletePerformanceMutation = useDeletePerformance();
  const deleteEventMutation = useDeleteEvent();
  const updateUnifiedPerformanceMutation = useUpdatePerformances();
  const createEventMutation = useCreateEvents();
  const updateEventMutation = useUpdateEvents();
  const createPerformanceEventMutation = useCreatePerformanceEvents();
  const performanceEventsClient = usePerformanceEventsClient();
  // Delete-event goes through useDeleteEvent, which removes the events
  // row and its `performance_events` junction rows; cascades on
  // `rodeo_round_configs.event_id` clean up the round configs too.
  const deletingEventMutation = useDeleteEvent();
  const [isSavingRounds, setIsSavingRounds] = useState(false);
  const queryClient = useQueryClient();

  // Hydrate the event-config dialog's rounds step from the DB when editing
  // an existing event config. Disabled on add because there's no id yet.
  const { data: existingRounds } = useRoundConfigsByEvent(
    editingEventId ?? undefined,
  );

  // Step 1: join rows (performance_id <-> event_id). Loaded unconditionally
  // because both the collapsed header (event count + aggregate prize pool)
  // and the expanded body need them. The downstream code derives a
  // legacy-shaped "event config" object from each hydrated event row so
  // the UI can stay unchanged.
  const { data: joinRows, isLoading: joinLoading } = useEventsByPerformance(
    performance.id,
  );

  // All round configs for this performance so event previews can show their
  // round counts without one request per event. Only needed when expanded
  // or the modal is open — round counts don't surface on the collapsed row.
  const { data: allPerfRounds } = useRoundConfigsByPerformance(
    expanded || showEditPerformance ? performance.id : undefined,
  );
  const roundCountByEventId = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of allPerfRounds ?? []) {
      const eid = (r as any).event_id as string | null | undefined;
      if (!eid) continue;
      counts[eid] = (counts[eid] ?? 0) + 1;
    }
    return counts;
  }, [allPerfRounds]);

  // Step 2: hydrate each event_id into a full events row. Using `useQueries`
  // gives us automatic cache-sharing — if another component (e.g. the rodeo
  // detail view) has already loaded an event by id, we get it from cache
  // instantly. Disabled until the join rows arrive.
  const eventIds = useMemo(
    () =>
      (joinRows ?? [])
        .map((jr) => jr.event_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    [joinRows],
  );

  // Hydrate events. Loaded unconditionally because the derivation below
  // builds the `events` aggregate the collapsed header relies on.
  const eventQueries = useQueries({
    queries: eventIds.map((id) => ({
      queryKey: eventsKeys.detail(id),
      queryFn: () => new EventsClient().getById(id),
      enabled: true,
      // Events rarely change while a delegate browses a rodeo — a short
      // stale time keeps expands snappy without causing stale data.
      staleTime: 30_000,
    })),
  });

  const eventsLoading = joinLoading || eventQueries.some((q) => q.isLoading);

  // Build a stable array of hydrated events aligned with `eventIds`.
  // We intentionally keep entries whose fetch failed / is still pending
  // so the row still renders with whatever we have (id + a placeholder
  // name). This is the "pragmatic fallback" from the coordinator plan.
  const hydratedEvents: Array<{ id: string; data: EventsRow | undefined }> =
    useMemo(
      () =>
        eventIds.map((id, idx) => ({
          id,
          data: eventQueries[idx]?.data,
        })),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [eventIds.join('|'), eventQueries.map((q) => q.dataUpdatedAt).join('|')],
    );

  // Derive the legacy-shaped `events` array from the hydrated events.
  // The shape carries `{ id, event_type, event_category, entry_fee,
  // transaction_fee, contestant_limit, metadata.prizes.total_prize_amount }`
  // so the rest of the component can stay unchanged.
  //
  // `metadata.source_event_config_id` is a back-compat key carried on
  // older seed rows that predate the unified-events refactor; when
  // present it identifies the same logical event and we use it as the
  // entity id so legacy editing paths keep targeting the right row.
  // New data has no such key, in which case we fall back to `events.id`.
  const events = useMemo(() => {
    return hydratedEvents
      .map(({ id, data: ev }) => {
        if (!ev) return null;
        // The trig_* columns aren't exposed on the generated EventsRow type
        // (they're not marked `public` in the entity config) but the API
        // payload contains them — we read through `any` like the rest of
        // this file does for backend-only fields.
        const evAny = ev as unknown as Record<string, unknown>;
        const meta = (ev.metadata as Record<string, unknown> | null) ?? {};
        const prizes = (meta.prizes as Record<string, unknown> | undefined) ?? {};
        const sourceConfigId =
          typeof meta.source_event_config_id === 'string'
            ? meta.source_event_config_id
            : id;
        return {
          id: sourceConfigId,
          event_type:
            (evAny.trig_event_type_name as string | null | undefined) ??
            (ev.event_name as string | null) ??
            '',
          event_category:
            (meta.event_category as 'timed' | 'scored' | undefined) ??
            'timed',
          entry_fee: ev.entry_fee ?? null,
          transaction_fee: ev.transaction_fee ?? null,
          contestant_limit: ev.max_contestants ?? null,
          non_member_surcharge:
            typeof meta.non_member_surcharge === 'number'
              ? meta.non_member_surcharge
              : Number(meta.non_member_surcharge) || 0,
          allow_multiple_registrations:
            !!(evAny.allow_multiple_registrations as boolean | null | undefined),
          max_registrations_per_user:
            (evAny.max_registrations_per_user as number | null | undefined) ?? null,
          metadata: {
            ...(typeof meta === 'object' ? meta : {}),
            prizes: {
              total_prize_amount:
                ev.total_prize_amount ??
                (prizes.total_prize_amount as number | undefined) ??
                0,
              adjustments: Array.isArray(prizes.adjustments)
                ? prizes.adjustments
                : [],
            },
          },
        };
      })
      .filter((c): c is NonNullable<typeof c> => c != null);
  }, [hydratedEvents]);

  const handleHeaderToggle = () => setExpanded((prev) => !prev);

  const handleDeletePerformanceConfirm = async () => {
    setDeletePerformanceError(null);
    try {
      await deletePerformanceMutation.mutateAsync(performance.id);
      onDeletePerformance?.(performance.id);
      // Only close the dialog on success — parent cache invalidation will
      // remove this component from the tree. On failure we stay open so
      // the user can see the error and decide whether to retry.
      setShowDeletePerformance(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete performance. Please try again.';
      setDeletePerformanceError(message);
      // Dialog intentionally stays open so the error is visible.
    }
  };

  const handleDeleteEventConfirm = async () => {
    if (!deleteEventId) return;
    const id = deleteEventId;
    setDeleteEventError(null);
    try {
      await deleteEventMutation.mutateAsync(id);
      onDeleteEvent?.(id);
      // Only close on success — leave dialog open with an error message on failure.
      setDeleteEventId(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete event. Please try again.';
      setDeleteEventError(message);
      // Dialog intentionally stays open so the error is visible.
    }
  };

  const editingEvent = editingEventId
    ? (events ?? []).find((c) => c.id === editingEventId)
    : undefined;

  const openEditEvent = (id: string) => {
    setEditingEventId(id);
    setEventDialogError(null);
    setEventDialogOpen(true);
  };

  const handleEventSubmit = async (values: RodeoEventDialogValues) => {
    setEventDialogError(null);
    try {
      // Empty-string numerics must go out as undefined so the TypeBox
      // schema accepts them; DB defaults (e.g. non_member_surcharge) fire.
      // barrel_racing is NOT NULL DEFAULT false in the DB but the generated
      // CRUD schema still marks it required, so we supply the default here.
      // Map the wizard form values into the unified `events` write
      // shape. Per-performance scope flows into `performance_events`
      // (the next step) rather than a column on the event row.
      const trimmedEventName = values.event_name.trim();
      const eventPayload = {
        rodeo_id: rodeoId,
        // Optional custom name — null when blank so the title resolver falls
        // back to the discipline (`trig_event_type_name`).
        event_name: trimmedEventName.length > 0 ? trimmedEventName : null,
        trig_event_type_name: values.event_type,
        entry_fee: values.entry_fee ?? 0,
        // Preserve null on "Reset to auto" so the backend payment service
        // falls back to the Stripe-derived fee. Coercing to 0 here would
        // make the EventCard render "Free" and the next open of the edit
        // dialog read 0 as a manual override instead of auto-calc.
        transaction_fee: values.transaction_fee,
        total_prize_amount: values.total_prize_amount ?? 0,
        // 0/blank both mean "Unlimited" → null. Without coercing 0 here,
        // a legacy event with max_contestants=0 would round-trip back
        // unchanged and the capacity check would treat it as always full.
        max_contestants:
          typeof values.contestant_limit === 'number' &&
          values.contestant_limit > 0
            ? values.contestant_limit
            : null,
        allow_multiple_registrations: !!values.allow_multiple_registrations,
        // CHECK constraint requires 1..10 and the backend's TypeBox
        // validator silently coerces nulls on numeric fields to 0, which
        // would trip the lower bound. Send 1 (the column default) when
        // multi-reg is off, and clamp to [1,10] when it's on so a stale
        // out-of-range value never escapes from the form.
        max_registrations_per_user: values.allow_multiple_registrations
          ? Math.min(10, Math.max(1, values.max_registrations_per_user ?? 1))
          : 1,
        metadata: {
          ...((editingEvent?.metadata as Record<string, unknown>) ?? {}),
          event_category: values.event_category,
          non_member_surcharge: values.non_member_surcharge ?? 0,
          prizes: {
            total_prize_amount: values.total_prize_amount,
            adjustments: values.prize_adjustments,
          },
        },
      };

      let eventId: string;
      if (editingEventId) {
        await updateEventMutation.mutateAsync({
          id: editingEventId,
          data: eventPayload as any,
        });
        eventId = editingEventId;
      } else {
        const created = await createEventMutation.mutateAsync(
          eventPayload as any,
        );
        eventId =
          (created as any)?.id ?? (created as any)?.data?.id ?? '';
      }

      // Link this event to the current performance via performance_events
      // (skip if a link already exists so we don't duplicate assignments
      // the event already holds to other perfs).
      if (eventId) {
        const existingUnifiedLinks = await performanceEventsClient.getAll({
          filters: {
            event_id: eventId,
            performance_id: performance.id,
          },
        } as any);
        const unifiedRows =
          (existingUnifiedLinks as any)?.data ??
          (Array.isArray(existingUnifiedLinks) ? existingUnifiedLinks : []);
        if (!unifiedRows.length) {
          await createPerformanceEventMutation.mutateAsync({
            event_id: eventId,
            performance_id: performance.id,
            max_contestants: null,
          } as any);
        }
      }

      // Sync rounds through the wizard's /round-configs workflow endpoint.
      // The server diff-syncs by round_number (update in place, insert new,
      // delete dropped LAST), so a rejected write can never leave the event
      // with zero rounds — the per-row delete-then-create this replaced did
      // exactly that on a live finals rodeo. run_order_mode is a delegate's
      // live-scoring choice, not this dialog's: carry the stored value
      // forward per round_number; a brand-new round gets the server default.
      if (eventId) {
        const priorRunOrderMode = new Map(
          (existingRounds ?? [])
            .filter((r) => r.round_number != null && r.run_order_mode)
            .map((r) => [r.round_number as number, r.run_order_mode as string]),
        );
        setIsSavingRounds(true);
        try {
          await httpClient.post(`/jackpot-ops/rodeo/${rodeoId}/round-configs`, {
            event_id: eventId,
            round_configs: values.rounds.map((r, i) => ({
              round_number: i + 1,
              round_name: r.round_name || null,
              // Keep whatever performance the round already carried; this
              // perf is only the default for rounds that had none. The Avg
              // row is a payout aggregate, not a run — it stays unassigned.
              performance_id: r.is_average
                ? undefined
                : (r.performance_key ?? performance.id),
              is_go_round: !!r.is_go_round,
              is_short_round: !!r.is_short_round,
              // Backend schema enforces >= 1; treat 0/null as "everyone
              // advances" and omit so the DB stores NULL.
              advancing_count:
                r.advancing_count && r.advancing_count > 0
                  ? r.advancing_count
                  : undefined,
              run_order_mode: priorRunOrderMode.get(i + 1),
              // Prize config stored as metadata.prizes on the round so it
              // can round-trip without a schema change. is_average rides
              // on the same jsonb blob — the UI uses it to render the
              // "AVG" label and hide the row's delete button.
              metadata: {
                is_average: !!r.is_average,
                prizes: {
                  has_prizes: !!r.has_prizes,
                  round_prize_total: Number(r.round_prize_total) || 0,
                  is_progressive: !!r.is_progressive,
                  places: r.places,
                },
              },
            })),
          });
        } finally {
          setIsSavingRounds(false);
        }
      }

      queryClient.invalidateQueries({ queryKey: eventsKeys.all });
      queryClient.invalidateQueries({ queryKey: rodeoRoundConfigsKeys.all });
      setEventDialogOpen(false);
      setEditingEventId(null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to save event. Please try again.';
      setEventDialogError(message);
    }
  };

  const openDeleteEvent = (id: string) => {
    setDeletingEventError(null);
    setDeletingEventId(id);
  };

  const handleDeletingEventConfirm = async () => {
    if (!deletingEventId) return;
    setDeletingEventError(null);
    try {
      await deletingEventMutation.mutateAsync(deletingEventId);
      queryClient.invalidateQueries({ queryKey: eventsKeys.all });
      setDeletingEventId(null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to delete event. Please try again.';
      setDeletingEventError(message);
    }
  };

  const handleEditPerformanceSubmit = async (values: PerformanceDialogValues) => {
    setEditPerformanceError(null);
    try {
      // Write directly to the unified `performances` table. start_datetime
      // collapses what the wizard form still presents as separate
      // performance_date + start_time fields.
      const startDatetime =
        values.performance_date && values.start_time
          ? `${values.performance_date}T${values.start_time}`
          : values.performance_date
            ? `${values.performance_date}T00:00:00`
            : null;
      await updateUnifiedPerformanceMutation.mutateAsync({
        id: performance.id,
        data: {
          performance_name: values.performance_name,
          start_datetime: startDatetime,
          max_contestants: values.default_max_contestants,
          is_slack: values.is_slack,
        } as any,
      });

      setShowEditPerformance(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save performance. Please try again.';
      setEditPerformanceError(message);
    }
  };

  // Schema renamed: performance_date + start_time merged into start_datetime.
  // Cast through any so legacy field reads continue to compile while the UI
  // is migrated to start_datetime.
  const performanceDate = formatDate((performance as any).performance_date ?? performance.start_datetime);
  const timeRange = formatTime((performance as any).start_time ?? performance.start_datetime);

  // When the row is rendered inside a discipline accordion the expand
  // behavior is suppressed — there are no nested events to show since
  // the caller has already drilled in by discipline.
  const headerInteractive = expandable
    ? {
        className:
          'flex items-center gap-3 px-3 py-2 cursor-pointer select-none bg-muted/40 hover:bg-muted transition-colors border-l-2 border-border',
        onClick: handleHeaderToggle,
        role: 'button' as const,
        tabIndex: 0,
        'aria-expanded': expanded,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleHeaderToggle();
          }
        },
      }
    : {
        className:
          'flex items-center gap-3 px-3 py-2 select-none bg-muted/40 border-l-2 border-border',
      };

  return (
    <div className="rounded-md border border-border/60 bg-background overflow-hidden">
      {/* Collapsible header row — secondary indent level. */}
      <div {...headerInteractive}>
        {expandable && (
          <ChevronRight
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
              expanded ? 'rotate-90' : ''
            }`}
          />
        )}

        {/* Name + date/time subtitle */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-sm truncate">
              {performance.performance_name}
            </h4>
            {performance.is_slack && (
              <span className="text-xs text-muted-foreground italic">
                Slack
              </span>
            )}
          </div>
          {(performanceDate || timeRange) && (
            // Prize pool is an event-level property (held on the parent
            // rodeo_event_config), not per-performance — the prize is paid
            // once across all performances of the discipline. The parent
            // EventSection already shows it, so the per-perf row
            // keeps just date + time to avoid duplicating the amount.
            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
              {performanceDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {performanceDate}
                </span>
              )}
              {timeRange && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {timeRange}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Action buttons — stopPropagation so they don't toggle the row. */}
        <div
          className="flex items-center gap-1 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Edit performance"
            onClick={() => {
              setEditPerformanceError(null);
              setShowEditPerformance(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            title="Delete performance"
            onClick={() => setShowDeletePerformance(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Expanded event list — reads from the unified `events` table (same
          source the Edit Performance modal uses). Skipped entirely when
          `expandable=false` (e.g. inside EventSection, where the caller
          already drilled in by discipline). */}
      {expandable && expanded && (
        <div className="px-3 py-2 space-y-2 border-l-2 border-border bg-background">
          {eventsLoading ? (
            <p className="text-sm text-muted-foreground italic">
              Loading events…
            </p>
          ) : !events || events.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No events in this performance.
            </p>
          ) : (
            events.map((ec) => (
              <PerformanceEventRow
                key={ec.id}
                config={ec}
                roundCount={roundCountByEventId[ec.id] ?? 0}
                prizeTotal={
                  ((ec.metadata as any)?.prizes?.total_prize_amount as
                    | number
                    | null
                    | undefined) ?? null
                }
                onEdit={() => openEditEvent(ec.id)}
                onDelete={() => openDeleteEvent(ec.id)}
              />
            ))
          )}
        </div>
      )}

      {/* Performance delete confirmation */}
      <Dialog
        open={showDeletePerformance}
        onOpenChange={(open) => {
          if (!open) {
            setShowDeletePerformance(false);
            setDeletePerformanceError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Performance</DialogTitle>
            <DialogDescription>
              This will permanently delete
              {performance.performance_name
                ? ` "${performance.performance_name}"`
                : ' this performance'}
              {' '}and ALL events within it. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletePerformanceError && (
            <p className="text-sm text-destructive px-1" role="alert">
              {deletePerformanceError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeletePerformance(false);
                setDeletePerformanceError(null);
              }}
              disabled={deletePerformanceMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePerformanceConfirm}
              disabled={deletePerformanceMutation.isPending}
            >
              {deletePerformanceMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event delete confirmation */}
      <Dialog
        open={!!deleteEventId}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteEventId(null);
            setDeleteEventError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteEventError && (
            <p className="text-sm text-destructive px-1" role="alert">
              {deleteEventError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteEventId(null);
                setDeleteEventError(null);
              }}
              disabled={deleteEventMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteEventConfirm}
              disabled={deleteEventMutation.isPending}
            >
              {deleteEventMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event-config delete confirmation — used by the expanded-list
          trash. Same shape as the performance/event confirm dialogs for
          consistency. */}
      <Dialog
        open={!!deletingEventId}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingEventId(null);
            setDeletingEventError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          {deletingEventError && (
            <p className="text-sm text-destructive px-1" role="alert">
              {deletingEventError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeletingEventId(null);
                setDeletingEventError(null);
              }}
              disabled={deletingEventMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletingEventConfirm}
              disabled={deletingEventMutation.isPending}
            >
              {deletingEventMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit performance modal — shared with RodeoWizard Step 2. The
          eventsSlot now uses PersistedEventTogglesSlot for assignment-only
          editing (no per-event edit/delete in the modal). Per-event edits
          still live on the expanded perf row's PerformanceEventRow. */}
      <PerformanceDialog
        open={showEditPerformance}
        onOpenChange={(open) => {
          setShowEditPerformance(open);
          if (!open) setEditPerformanceError(null);
        }}
        mode="edit"
        initialValues={{
          performance_name: performance.performance_name ?? '',
          performance_date: ((performance as any).performance_date as string | null) ?? '',
          start_time: ((performance as any).start_time as string | null) ?? '',
          // end_time removed from the UI — dialog no longer collects it.
          end_time: null,
          is_slack: !!performance.is_slack,
          default_max_contestants: (performance as any).default_max_contestants ?? null,
        }}
        onSubmit={handleEditPerformanceSubmit}
        errorMessage={editPerformanceError}
        isSaving={updateUnifiedPerformanceMutation.isPending}
        eventsSlot={
          <PersistedEventTogglesSlot
            performanceId={performance.id}
            rodeoId={rodeoId}
          />
        }
      />

      {/* Nested event add/edit modal — opened from PerformanceEventRow in
          the expanded list view (NOT from the Edit Performance modal). The
          modal slot now uses PersistedEventTogglesSlot for assignment;
          per-event edits still happen here. Writes go directly to `events`
          via the generated CRUD hooks. */}
      <RodeoEventDialog
        open={eventDialogOpen}
        onOpenChange={(open) => {
          setEventDialogOpen(open);
          if (!open) {
            setEditingEventId(null);
            setEventDialogError(null);
          }
        }}
        mode={editingEventId ? 'edit' : 'add'}
        initialValues={
          editingEvent
            ? (() => {
              const prizesMeta =
                ((editingEvent.metadata as any)?.prizes ?? {}) as {
                  total_prize_amount?: number | null;
                  adjustments?: Array<{
                    id?: string;
                    operation: 'add' | 'subtract';
                    adjustment_type: 'amount' | 'percentage';
                    amount?: number;
                    percentage?: number;
                    reason: string;
                  }>;
                };
              return {
                event_type: editingEvent.event_type ?? '',
                event_category:
                  (editingEvent.event_category as 'timed' | 'scored') ??
                  'timed',
                entry_fee: editingEvent.entry_fee ?? null,
                transaction_fee: editingEvent.transaction_fee ?? null,
                contestant_limit: editingEvent.contestant_limit ?? null,
                non_member_surcharge: editingEvent.non_member_surcharge ?? null,
                allow_multiple_registrations:
                  !!(editingEvent as any).allow_multiple_registrations,
                max_registrations_per_user:
                  (editingEvent as any).max_registrations_per_user ?? null,
                performance_assignments: [
                  { performance_key: performance.id, max_contestants: null },
                ],
                total_prize_amount: prizesMeta.total_prize_amount ?? null,
                prize_adjustments: (prizesMeta.adjustments ?? []).map((a) => ({
                  id:
                    a.id ??
                    (typeof crypto !== 'undefined' && 'randomUUID' in crypto
                      ? crypto.randomUUID()
                      : `adj-${Math.random()}`),
                  operation: a.operation,
                  adjustment_type: a.adjustment_type,
                  amount: Number(a.amount) || 0,
                  percentage: Number(a.percentage) || 0,
                  reason: a.reason,
                })),
                rounds: (existingRounds ?? [])
                  .slice()
                  // Sort Avg to the end regardless of round_number so the
                  // dialog's "Avg pinned to the end" invariant holds even
                  // when a seed/import gave it a mid-range round_number.
                  .sort((a, b) => {
                    const aAvg = !!((a.metadata as any)?.is_average);
                    const bAvg = !!((b.metadata as any)?.is_average);
                    if (aAvg !== bAvg) return aAvg ? 1 : -1;
                    return (a.round_number ?? 0) - (b.round_number ?? 0);
                  })
                  .map((r) => {
                    // Prize config + is_average flag are stored in the
                    // round's metadata jsonb — is_average rides next to
                    // the `prizes` sub-object, not inside it.
                    const rawMeta = (r.metadata as any) ?? {};
                    const meta = (rawMeta.prizes ?? {}) as {
                      has_prizes?: boolean;
                      round_prize_total?: number;
                      is_progressive?: boolean;
                      places?: { place: number; percentage: number }[];
                    };
                    return {
                      round_number: r.round_number ?? 1,
                      round_name: r.round_name ?? '',
                      is_go_round: !!r.is_go_round,
                      is_short_round: !!r.is_short_round,
                      advancing_count: r.advancing_count ?? null,
                      is_average: !!rawMeta.is_average,
                      // Explicit per-round performance. This surface is scoped
                      // to a single perf and doesn't render the picker, but the
                      // value still has to round-trip — saving must not stomp a
                      // mapping made in the wizard or on My Rodeos.
                      performance_key: rawMeta.is_average
                        ? null
                        : (r.performance_id ?? null),
                      has_prizes: !!meta.has_prizes,
                      round_prize_total: Number(meta.round_prize_total) || 0,
                      is_progressive: !!meta.is_progressive,
                      places: Array.isArray(meta.places) ? meta.places : [],
                    };
                  }),
              };
            })()
            : {
                performance_assignments: [
                  { performance_key: performance.id, max_contestants: null },
                ],
                rounds: [],
              }
        }
        onSubmit={handleEventSubmit}
        errorMessage={eventDialogError}
        isSaving={
          createEventMutation.isPending ||
          updateEventMutation.isPending ||
          isSavingRounds
        }
        // Rodeo-wide uniqueness after the one-config-per-event-type refactor.
        // We only have this perf's configs to work with here, but that's a
        // close-enough approximation — users editing from the delegate page
        // don't typically create event types outside what's already scoped
        // to this perf. Exclude the config currently being edited so the
        // user can keep its own type selected.
        usedEventTypes={(events ?? [])
          .filter((ec) => ec.id !== editingEventId)
          .map((ec) => ec.event_type ?? '')
          .filter(Boolean)}
      />
    </div>
  );
}


/* ───────────────────── Event Config Row ───────────────────── */

interface PerformanceEventRowProps {
  config: {
    id: string;
    event_type: string | null;
    event_category: string | null;
    entry_fee: number | null;
    transaction_fee: number | null;
    contestant_limit: number | null;
  };
  /** Number of rounds_configs linked to this event config. */
  roundCount: number;
  /** Event-level prize pool, from metadata.prizes.total_prize_amount. */
  prizeTotal: number | null;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Single row rendering a rodeo_event_config inside the expanded
 * PerformanceSection body. Mirrors EventRow's visual treatment but reads
 * directly from event-config columns (event_type / event_category /
 * entry_fee) because drafts have no events-table rows yet.
 */
function PerformanceEventRow({
  config,
  roundCount,
  prizeTotal,
  onEdit,
  onDelete,
}: PerformanceEventRowProps) {
  return (
    <div
      className="rounded-lg border-2 border-border bg-muted/30 p-3 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors group"
      data-event-config-id={config.id}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Trophy className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span className="font-medium text-sm truncate">
              {config.event_type ?? 'Event'}
            </span>
            {config.event_category && (
              <span className="text-xs text-muted-foreground capitalize">
                {config.event_category}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {config.entry_fee != null
              ? `$${config.entry_fee} entry`
              : 'No entry fee'}
            {config.transaction_fee != null
              ? ` + $${config.transaction_fee} txn`
              : ''}
            {config.contestant_limit != null
              ? ` · Max ${config.contestant_limit}`
              : ''}
            {roundCount > 0
              ? ` · ${roundCount} round${roundCount !== 1 ? 's' : ''}`
              : ''}
            {prizeTotal != null && prizeTotal > 0
              ? ` · $${prizeTotal} prize`
              : ''}
          </p>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Edit event"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
            title="Delete event"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PerformanceSection;
